'use client';

import { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useUser } from '@/shared/providers/user-provider/use-user';

export type WalletInitialStep = 'deposit' | 'withdraw' | 'history-detail';

interface AppShellQueryParamsInitializerProps {
  emailCodeAuthenticate: (
    code: string,
    fromProfile?: boolean | string
  ) => Promise<unknown>;
  isAuth: boolean;
  onAuthManagementOpen: () => void;
  onWalletInitialStepChange: (step: WalletInitialStep) => void;
  onWalletOpen: () => void;
}

const removeAllQueryParams = () => {
  const cleanUrl = window.location.origin + window.location.pathname;
  window.history.replaceState({}, '', cleanUrl);
};

const removeIsSessionExpiredFromUrl = () => {
  const nextSearchParams = new URLSearchParams(window.location.search);
  nextSearchParams.delete('isSessionExpired');

  const queryString = nextSearchParams.toString();
  const newUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;

  window.history.replaceState(null, '', newUrl);
};

const AppShellQueryParamsInitializer = ({
  emailCodeAuthenticate,
  isAuth,
  onAuthManagementOpen,
  onWalletInitialStepChange,
  onWalletOpen,
}: AppShellQueryParamsInitializerProps) => {
  const searchParams = useSearchParams();
  const { user, setUser } = useUser();

  const confirmationCodeParam = searchParams.get('confirmationCode');
  const triggerFromProfileParam = searchParams.get('triggerFromProfile');
  const isSessionExpired = searchParams.get('isSessionExpired');
  const refillBalanceParam = searchParams.get('refillBalance');
  const withdrawParam = searchParams.get('withdraw');
  const depositParam = searchParams.get('deposit');
  const openPaymentParam = searchParams.get('openPayment');

  useEffect(() => {
    const handleQueryParams = async () => {
      if (!confirmationCodeParam) return;

      if (isAuth && triggerFromProfileParam) {
        try {
          await emailCodeAuthenticate(
            confirmationCodeParam,
            triggerFromProfileParam
          );
          removeAllQueryParams();
        } catch (error) {
          console.error('Error during email authentication:', error);
        }
        return;
      }

      if (!isAuth) {
        await emailCodeAuthenticate(confirmationCodeParam);
      }
    };

    handleQueryParams();
  }, [confirmationCodeParam, triggerFromProfileParam, isAuth]);

  useEffect(() => {
    if (!isAuth && isSessionExpired) {
      onAuthManagementOpen();
      removeIsSessionExpiredFromUrl();
    }
  }, [isAuth, isSessionExpired]);

  useEffect(() => {
    if (!isAuth) return;

    const nextStep: WalletInitialStep | null = withdrawParam
      ? 'withdraw'
      : openPaymentParam
        ? 'history-detail'
        : depositParam || refillBalanceParam
          ? 'deposit'
          : null;

    if (!nextStep) return;

    onWalletInitialStepChange(nextStep);
    onWalletOpen();
  }, [
    depositParam,
    isAuth,
    onWalletInitialStepChange,
    onWalletOpen,
    openPaymentParam,
    refillBalanceParam,
    withdrawParam,
  ]);

  useEffect(() => {
    if (!isAuth || typeof window === 'undefined') return;

    if (sessionStorage.getItem('welcomeBonus')) {
      onWalletInitialStepChange('deposit');
      onWalletOpen();
      sessionStorage.removeItem('welcomeBonus');
    }
  }, [isAuth, onWalletInitialStepChange, onWalletOpen]);

  useEffect(() => {
    if (!isAuth || !user.isBonusDepositReplenish) return;

    onWalletInitialStepChange('deposit');
    onWalletOpen();
    setUser({
      ...user,
      isBonusDepositReplenish: false,
    });
  }, [isAuth, onWalletInitialStepChange, onWalletOpen, setUser, user]);

  return null;
};

export { AppShellQueryParamsInitializer };
